import { Link } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import { COMPANY } from "../data/siteData";

export default function PrivatlivspolitikPage() {
  return (
    <div className="bg-[#070d18] text-slate-100 min-h-screen">
      <SEOHead
        title="Privatlivspolitik"
        description="Læs om, hvordan KVC Express ApS behandler og beskytter personoplysninger indsamlet via kontaktformularen på hjemmesiden."
      />

      {/* Hero Header */}
      <section className="relative pt-16 pb-20 lg:pt-24 lg:pb-28 border-b border-slate-800 bg-[#081224] overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-[#0F766E]/20 border border-[#2DD4BF]/40 text-[#2DD4BF] text-xs font-mono uppercase tracking-widest">
              <i className="fa-solid fa-shield-halved text-[10px]" aria-hidden="true" />
              <span>Databeskyttelse & Fortrolighed</span>
            </div>

            <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight leading-tight font-display">
              Privatlivspolitik
            </h1>

            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
              Her kan du læse om, hvordan KVC Express ApS behandler dine person- og virksomhedsoplysninger, når du benytter vores kontaktformular eller retter henvendelse til os.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 sm:py-20 bg-[#070d18]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#0b1426] border border-slate-800 rounded-2xl p-6 sm:p-10 lg:p-12 shadow-xl space-y-10">
            
            {/* Introduktion */}
            <div className="space-y-3 pb-8 border-b border-slate-800">
              <h2 className="text-xl sm:text-2xl font-bold text-white font-display">
                1. Dataansvarlig
              </h2>
              <p className="text-slate-300 text-sm leading-relaxed">
                KVC Express ApS er dataansvarlig for behandlingen af de personoplysninger, som vi modtager, når du henvender dig til os via kontaktformularen, telefon eller e-mail.
              </p>
              <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800 text-sm text-slate-300 space-y-1">
                <div><strong className="text-white">{COMPANY.name}</strong></div>
                <div>CVR-nr.: {COMPANY.cvr}</div>
                <div>Område: {COMPANY.location} ({COMPANY.coverage})</div>
                <div>E-mail: <a href={`mailto:${COMPANY.email}`} className="text-[#2DD4BF] hover:underline">{COMPANY.email}</a></div>
                <div>Telefon: <a href={`tel:${COMPANY.phoneRaw}`} className="text-[#2DD4BF] hover:underline">{COMPANY.phone}</a></div>
              </div>
            </div>

            {/* Oplysninger indsamlet */}
            <div className="space-y-3 pb-8 border-b border-slate-800">
              <h2 className="text-xl sm:text-2xl font-bold text-white font-display">
                2. Oplysninger vi indsamler via kontaktformularen
              </h2>
              <p className="text-slate-300 text-sm leading-relaxed">
                Når du benytter kontaktformularen på vores hjemmeside til at forespørge på faste transportaftaler, indsamler vi følgende oplysninger:
              </p>
              <ul className="list-disc list-inside space-y-1.5 text-sm text-slate-300 pl-2">
                <li>Navn på kontaktperson</li>
                <li>Virksomhed / organisation</li>
                <li>Telefonnummer</li>
                <li>E-mailadresse</li>
                <li>Ønsket transporttype (medicinaltransport, køletransport, fødevaretransport m.v.)</li>
                <li>Din beskrivelse af det ønskede transportbehov eller samarbejde</li>
              </ul>
            </div>

            {/* Formål */}
            <div className="space-y-3 pb-8 border-b border-slate-800">
              <h2 className="text-xl sm:text-2xl font-bold text-white font-display">
                3. Formål med behandlingen
              </h2>
              <p className="text-slate-300 text-sm leading-relaxed">
                Vi behandler dine oplysninger udelukkende med følgende formål:
              </p>
              <ul className="list-disc list-inside space-y-1.5 text-sm text-slate-300 pl-2">
                <li>At besvare din henvendelse og besvare spørgsmål om vores transportydelser.</li>
                <li>At afdække jeres transportbehov og udarbejde uforpligtende tilbud eller samarbejdsaftaler.</li>
                <li>At opretholde en professionel dialog forud for og under et eventuelt samarbejde.</li>
              </ul>
            </div>

            {/* Retsgrundlag */}
            <div className="space-y-3 pb-8 border-b border-slate-800">
              <h2 className="text-xl sm:text-2xl font-bold text-white font-display">
                4. Retsgrundlag for behandlingen
              </h2>
              <p className="text-slate-300 text-sm leading-relaxed">
                Retsgrundlaget for vores behandling af dine oplysninger følger af:
              </p>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-start gap-2">
                  <i className="fa-solid fa-check text-[#2DD4BF] text-xs mt-1" aria-hidden="true" />
                  <span>
                    <strong className="text-white">GDPR art. 6, stk. 1, litra b:</strong> Behandling er nødvendig af hensyn til opfyldelse af en kontrakt eller forudgående tiltag på din anmodning forud for indgåelse af en transportaftale.
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="fa-solid fa-check text-[#2DD4BF] text-xs mt-1" aria-hidden="true" />
                  <span>
                    <strong className="text-white">GDPR art. 6, stk. 1, litra f:</strong> Vores legitime interesse i at besvare henvendelser fra potentielle og eksisterende erhvervskunder samt yde god kundeservice.
                  </span>
                </li>
              </ul>
            </div>

            {/* Opbevaring og sletning */}
            <div className="space-y-3 pb-8 border-b border-slate-800">
              <h2 className="text-xl sm:text-2xl font-bold text-white font-display">
                5. Opbevaring og sletning af data
              </h2>
              <p className="text-slate-300 text-sm leading-relaxed">
                Vi opbevarer kun dine oplysninger, så længe det er nødvendigt for at opfylde det formål, de blev indsamlet til:
              </p>
              <ul className="list-disc list-inside space-y-1.5 text-sm text-slate-300 pl-2">
                <li>Hvis en henvendelse ikke fører til et samarbejde, slettes oplysningerne senest inden for 12 måneder, medmindre der er saglig grund til fortsat dialog.</li>
                <li>Fører henvendelsen til en aftale om transport, opbevares relevante kunde- og regnskabsdata i overensstemmelse med gældende lovgivning, herunder bogføringsloven (op til 5 år).</li>
              </ul>
            </div>

            {/* Tredjeparter og databehandlere */}
            <div className="space-y-3 pb-8 border-b border-slate-800">
              <h2 className="text-xl sm:text-2xl font-bold text-white font-display">
                6. Modtagere og videregivelse
              </h2>
              <p className="text-slate-300 text-sm leading-relaxed">
                Vi videregiver eller sælger aldrig dine oplysninger til tredjemand til markedsføringsformål. Dine oplysninger behandles fortroligt og videregives udelukkende til betroede databehandlere (fx leverandører af webhosting og formularinfrastruktur), som vi har indgået databehandleraftaler med, og som behandler data i overensstemmelse med GDPR.
              </p>
            </div>

            {/* Sikkerhed */}
            <div className="space-y-3 pb-8 border-b border-slate-800">
              <h2 className="text-xl sm:text-2xl font-bold text-white font-display">
                7. Sikkerhed
              </h2>
              <p className="text-slate-300 text-sm leading-relaxed">
                Vi har truffet passende tekniske og organisatoriske sikkerhedsforanstaltninger mod, at dine oplysninger hændeligt eller ulovligt bliver slettet, offentliggjort, fortabt, forringet eller kommer til uvedkommendes kendskab. Al dataoverførsel via formularen på hjemmesiden sker krypteret via SSL/HTTPS.
              </p>
            </div>

            {/* Dine rettigheder */}
            <div className="space-y-3 pb-8 border-b border-slate-800">
              <h2 className="text-xl sm:text-2xl font-bold text-white font-display">
                8. Dine rettigheder
              </h2>
              <p className="text-slate-300 text-sm leading-relaxed">
                I henhold til databeskyttelsesforordningen har du en række rettigheder i forhold til vores behandling af dine oplysninger:
              </p>
              <ul className="list-disc list-inside space-y-1.5 text-sm text-slate-300 pl-2">
                <li><strong>Ret til indsigt:</strong> Du har ret til at få indsigt i, hvilke personoplysninger vi behandler om dig.</li>
                <li><strong>Ret til berigtigelse:</strong> Du har ret til at få urigtige oplysninger rettet.</li>
                <li><strong>Ret til sletning:</strong> I visse tilfælde har du ret til at få oplysninger slettet forud for det tidspunkt, hvor vi normalt ville slette dem.</li>
                <li><strong>Ret til begrænsning og indsigelse:</strong> Du kan i visse tilfælde gøre indsigelse mod vores behandling eller anmode om begrænsning af behandlingen.</li>
              </ul>
              <p className="text-slate-300 text-sm leading-relaxed pt-2">
                Ønsker du at gøre brug af dine rettigheder, er du velkommen til at kontakte os på <a href={`mailto:${COMPANY.email}`} className="text-[#2DD4BF] hover:underline font-semibold">{COMPANY.email}</a>.
              </p>
              <p className="text-slate-400 text-xs leading-relaxed">
                Du har også ret til at indgive en klage til Datatilsynet (<a href="https://www.datatilsynet.dk" target="_blank" rel="noopener noreferrer" className="text-[#2DD4BF] hover:underline">www.datatilsynet.dk</a>), hvis du er utilfreds med den måde, vi behandler dine personoplysninger på.
              </p>
            </div>

            {/* Opdatering */}
            <div className="space-y-3">
              <h2 className="text-xl sm:text-2xl font-bold text-white font-display">
                9. Ændringer til denne privatlivspolitik
              </h2>
              <p className="text-slate-300 text-sm leading-relaxed">
                Vi opdaterer denne privatlivspolitik, hvis der sker væsentlige ændringer i vores procedurer eller i lovgivningen. Den senest opdaterede version vil altid være tilgængelig på denne side.
              </p>
              <div className="text-xs font-mono text-slate-500 pt-2">
                Senest opdateret: Oktober 2026
              </div>
            </div>

            {/* CTA tilbage til kontakt */}
            <div className="pt-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-xs text-slate-400">
                Har du spørgsmål til vores transportløsninger eller databehandling?
              </div>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#0F766E] hover:bg-[#115E59] text-white text-xs font-semibold uppercase tracking-wider transition"
              >
                <span>Gå til kontakt</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
}
